/**
 * =============================================================================
 * Main Entry Point - Phaser Game Configuration
 * =============================================================================
 * 
 * RENDERING CONFIGURATION FOR PIXEL-PERFECT 2D MMORPG
 * ====================================================
 * 
 * Key settings for smooth, crisp pixel art rendering:
 * 
 * - pixelArt: true    → Nearest-neighbor filtering for crisp textures
 * - antialias: false  → No smoothing that would blur pixel art
 * - roundPixels: true → Sprites render at integer positions (no subpixel jitter)
 * - Scale.NONE        → Fixed resolution, no automatic scaling that could cause blur
 * 
 * The game uses a fixed logical resolution of VIEW_TILES_X × VIEW_TILES_Y tiles,
 * which ensures consistent visuals across different screen sizes.
 * 
 * =============================================================================
 */

import Phaser from 'phaser';
// Side-effect: override de los factories de texto/bitmapText/container para que TODO el texto
// del juego (labels, HUD, overhead, ventanas) quede SIEMPRE en posición entera (pixel-snap).
// Debe correr ANTES de crear cualquier Phaser.Game. Ver ui/snapGameObjects.ts.
import './ui/snapGameObjects';
import { GameScene } from './scenes/GameScene';
import { AuthApi } from './api/AuthApi';
import { AuthUI } from './ui/AuthUI';
import { installGlobalCursors } from './ui/cursors';
import { registerAssetBundleSW, assetsReady } from './boot/assetBundle';
import { installPerfProbe, setPerfPhase } from './boot/perfProbe';
import { preloadGameFonts } from './boot/fonts';
import { loadMap1Raw } from './boot/mapData';
import { mountLoadingMusic, unmountLoadingMusic } from './audio/loadingMusic';
import { AUREUM_GAME, TILE_SIZE, VIEW_TILES_X, VIEW_TILES_Y, SITE_URL, setAssetsBaseUrl } from '@shared/constants';

// Título de la pestaña = nombre del juego (constante, nunca hardcodeado). El <title>
// estático de index.html es sólo el fallback pre-JS.
document.title = AUREUM_GAME;

/**
 * Config en runtime: el front lee /config.json al boot para saber a qué backend
 * conectarse y de dónde tomar los assets. Así, en producción, cambiar el backend
 * (cuando se levante el droplet) o mover los assets a un bucket es editar
 * config.json + redeploy del static — SIN rebuild. En dev local no existe (o
 * apunta a localhost) y caemos a estos defaults.
 */
interface RuntimeConfig {
  serverUrl: string; // WebSocket del server Colyseus (wss://api.aureum.cc en prod)
  apiUrl: string; // REST de auth (https://api.aureum.cc en prod)
  assetsBaseUrl: string; // base de assets ('/assets' bundleado, o el bucket)
}

async function loadRuntimeConfig(): Promise<RuntimeConfig> {
  const defaults: RuntimeConfig = {
    serverUrl: 'ws://localhost:9000',
    apiUrl: 'http://localhost:9000',
    assetsBaseUrl: '/assets',
  };
  try {
    const res = await fetch('/config.json', { cache: 'no-store' });
    if (!res.ok) return defaults;
    const cfg = (await res.json()) as Partial<RuntimeConfig>;
    return {
      serverUrl: cfg.serverUrl || defaults.serverUrl,
      apiUrl: cfg.apiUrl || defaults.apiUrl,
      assetsBaseUrl: cfg.assetsBaseUrl || defaults.assetsBaseUrl,
    };
  } catch {
    return defaults;
  }
}

const config: Phaser.Types.Core.GameConfig = {
  type: Phaser.AUTO,
  backgroundColor: '#000000',
  parent: 'game-container',
  scale: {
    // Resolución lógica fija en tiles (= rango de visión). Modo FIT SIEMPRE: Phaser mantiene
    // la resolución lógica fija y CSS-escala el canvas para caber en el contenedor conservando
    // el aspecto. En ventana normal el contenedor mide EXACTAMENTE width×height (1280×960), así
    // que el factor de escala es 1.0 → sin resampling, pixel-perfect, idéntico a NONE. En pantalla
    // completa el contenedor crece al viewport (clase .fullscreen) y FIT estira el canvas SIN tocar
    // el rango de visión. Se usa FIT fijo (no se conmuta el modo en runtime) porque alternar
    // NONE↔FIT dejaba estilos de canvas a medias al salir de fullscreen (juego "bugeado").
    mode: Phaser.Scale.FIT,
    width: VIEW_TILES_X * TILE_SIZE,
    height: VIEW_TILES_Y * TILE_SIZE,
    autoCenter: Phaser.Scale.CENTER_BOTH,
    zoom: 1,
    // La Fullscreen API (botón de pantalla completa) se pide sobre #game-container, el mismo
    // elemento que estilamos en modo fullscreen. Ver GameScene.syncFullscreenDisplay.
    fullscreenTarget: 'game-container',
  },
  render: {
    // CRITICAL: These three settings work together for pixel-perfect rendering
    pixelArt: true,    // Uses nearest-neighbor filtering (no texture blur)
    antialias: false,  // Disables smoothing (keeps edges sharp)
    roundPixels: true, // Snaps all sprites to integer positions (prevents subpixel jitter)
  },
  loader: {
    // Cargar imágenes con <img src> en vez del default XHR+Blob. El path XHR
    // decodifica el PNG SINCRÓNICO dentro del onload del object-URL (bloquea el
    // main thread: PNGs grandes como el atlas de partículas 4608×4608 tardaban
    // ~2s ⇒ '[Violation] load handler took 2000ms'). Con HTMLImageElement el
    // browser decodifica con su pipeline nativo (async, fuera del main thread) y
    // el onload de JS queda liviano. Ver docs/ASSET_BUNDLING.md.
    imageLoadType: 'HTMLImageElement',
    // OBLIGATORIO junto con imageLoadType: el XHR+Blob producía object-URLs
    // same-origin (nunca tainted), pero <img src> a un CDN cross-origin
    // (ASSETS_CDN_URL en prod) queda tainted sin esto → WebGL texImage2D y todo
    // getImageData (recolor de body/globos del HUD) tiran SecurityError. El CDN
    // ya manda ACAO porque el path XHR actual también lo exige.
    crossOrigin: 'anonymous',
  },
  physics: {
    default: 'arcade',
  },
  disableContextMenu: true,
  scene: [GameScene],
};

async function bootstrap(): Promise<void> {
  // Probe de performance DEV-only: caza los freezes del hilo principal (long tasks) por fase.
  // Ver client/src/boot/perfProbe.ts. No hace nada en prod.
  installPerfProbe();
  setPerfPhase('login');

  // Cursores custom (.cur) a nivel documento: aplican en el login y todo el DOM, no sólo
  // en el canvas del juego. Antes de renderizar AuthUI para que el login ya nazca con ellos.
  installGlobalCursors();

  // Música de la fase de carga/login (intro) + botón flotante de música. Arranca en el primer gesto
  // del usuario (autoplay del browser). Usa el AudioManager compartido con el juego. Ver
  // docs/SOUND_AND_MUSIC.md.
  mountLoadingMusic();

  // Desactivar el menú contextual del navegador (click derecho) en TODO el documento — no sólo
  // en el canvas (Phaser ya lo hace ahí con disableContextMenu). Esto NO interfiere con la lógica
  // de click derecho del juego/UI, que usa eventos pointerdown (rightButtonDown), no `contextmenu`.
  // En dev lo dejamos pasar para poder inspeccionar la UI DOM (login/creación) con click derecho.
  if (!import.meta.env.DEV) {
    window.addEventListener('contextmenu', (e) => e.preventDefault());
  }

  // Actualizar título y link usando la constante SITE_URL. El link ahora muestra el LOGO
  // (img #aureum-link-logo, ver index.html), así que NO tocamos su contenido: solo el destino
  // y la etiqueta accesible.
  document.title = SITE_URL;
  const link = document.getElementById('aureum-link');
  if (link) {
    link.setAttribute('href', `https://${SITE_URL}`);
    link.setAttribute('aria-label', SITE_URL);
  }

  // Cargar config de runtime (backend + assets) antes de armar auth/juego.
  const runtime = await loadRuntimeConfig();
  setAssetsBaseUrl(runtime.assetsBaseUrl);

  // Disparar la descarga de map1.json (~35 MB) YA, en paralelo con el login/assets/fonts. Antes se
  // horneaba en el bundle JS; ahora es un asset aparte que se fetchea acá. NO bloquea: el login se
  // muestra apenas abajo; el gate real es `await mapReady`, justo antes de arrancar Phaser (el
  // warmup del mundo necesita el mapa en memoria). Ver boot/mapData.ts + PERFORMANCE_OPTIMIZATION.md.
  const mapReady = loadMap1Raw();

  // Registrar el Service Worker del bundle de assets (prod-only). NO bloquea: el
  // paquete se baja en paralelo mientras el usuario está en el login. El gate real
  // es `await assetsReady`, justo antes de arrancar Phaser.
  registerAssetBundleSW(runtime.assetsBaseUrl);

  // Disparar la descarga de las web fonts del juego YA, en paralelo con el login y
  // los assets. NO bloquea: el gate real es `await fontsReady`, justo antes de
  // arrancar Phaser, para que los labels (nombres de player/NPC) nazcan con la
  // tipografía correcta y no con el fallback. Ver client/src/boot/fonts.ts.
  const fontsReady = preloadGameFonts();

  const api = new AuthApi(runtime.apiUrl);
  const authUi = new AuthUI(api, runtime.serverUrl);

  // Mostrar el login/selección YA y correrlo EN PARALELO con el warmup del juego.
  // authUi.run() resuelve recién cuando el jugador elige personaje: NO lo esperamos
  // todavía. Mientras el usuario navega el login, arrancamos Phaser por detrás para
  // hacer todo el trabajo pesado (preload de texturas, animaciones, superficies,
  // pre-render del mapa), así el pase de la lista de personajes al mundo es casi
  // instantáneo. GameScene espera su gate `characterReady` antes de joinear la room.
  // Ver docs/AUTH_AND_LOGIN.md (warmup durante el login).
  const authRunPromise = authUi.run();

  // Crear el juego OCULTO detrás de la pantalla de selección (auth-root está por
  // encima de #game-container). Así el juego carga por detrás sin flash de su
  // propia pantalla de carga; cuando el mundo está listo hacemos el focus
  // cross-fade que revela la ventana. Ver docs/AUTH_AND_LOGIN.md.
  const gameContainer = document.getElementById('game-container');
  if (gameContainer) gameContainer.style.opacity = '0';

  // Gate: el warmup de GameScene (preload/create) lee el mapa síncrono vía getMap1Raw(), así que
  // Phaser no puede arrancar hasta tener map1.json en memoria. El login ya está visible (authUi.run
  // se llamó arriba), así que esta espera queda oculta detrás de la pantalla de selección.
  await mapReady;

  const game = new Phaser.Game(config);

  // `authResult` existe recién cuando el jugador elige personaje (ahí se crea la
  // barra DOM de transición). Buffereamos el último progreso del warmup para
  // aplicarlo apenas exista la barra; setProgress es monotónico, no retrocede.
  type RunResult = Awaited<typeof authRunPromise>;
  let authResult: RunResult | null = null;
  let lastProgress = 0;

  // Alimentar la barra que reemplazó al botón "Entrar" con el progreso real del juego.
  game.events.on('game:progress', (p: number) => {
    lastProgress = p;
    authResult?.setTransitionProgress(p);
  });

  // Mundo cargado y renderizado: revelar la ventana del juego con el focus
  // cross-fade y recién ahí destruir el UI de selección.
  game.events.once('game:ready', () => {
    setPerfPhase('ingame');
    if (gameContainer) gameContainer.style.opacity = '1';
    // El juego toma la instancia de audio compartida (música de zona crossfadea desde la intro);
    // sacamos el botón/listeners de la fase de carga (in-game se controla desde Opciones).
    unmountLoadingMusic();
    const r = authResult;
    if (r) void r.revealGame().then(() => r.destroyUI());
  });

  // Esperar a que el bundle esté listo (SW controlando la página) antes de que el
  // preload de Phaser encole sus cargas, así pegan al cache local en vez de a la red.
  // Y a que las web fonts estén bajadas, así los nombres de player/NPC se rasterizan
  // con la tipografía correcta desde el primer frame (no con el fallback). Recién ahí
  // arrancar el warmup de GameScene (sin characterId → el gate queda cerrado hasta
  // que el jugador elige personaje).
  const warmupStarted = Promise.all([assetsReady, fontsReady]).then(() => {
    game.scene.start('GameScene', { serverUrl: runtime.serverUrl });
  });

  // Esperar la selección de personaje (tiempo humano; el warmup ya viene corriendo).
  authResult = await authRunPromise;
  authResult.setTransitionProgress(lastProgress);

  // Garantizar que el warmup ya arrancó (scene.start llamado) y entregar el
  // personaje elegido, que desbloquea el join en GameScene.create().
  await warmupStarted;
  setPerfPhase('entrar'); // desde acá: join + burst de entidades + primer render
  const scene = game.scene.getScene('GameScene') as GameScene | undefined;
  scene?.provideCharacter(authResult.character.id);
}

window.addEventListener('load', () => {
  void bootstrap();
});

